/**
 * BrainEvent — provider-neutral event contract for AnathemaBrain's agent system.
 *
 * These types are the ONLY contract between the agent loop (main process) and any
 * frontend (Electron renderer today, Ink CLI tomorrow). They are intentionally
 * standalone: no Electron imports, no project imports. Every payload is plain,
 * JSON-serializable data so events can cross the IPC boundary or a socket
 * untouched.
 *
 * @see docs/plans/2026-06-28-zelari-code.md (Task 11.1)
 */

/** Discriminator for every {@link BrainEvent} variant. */
export type BrainEventType =
  | 'agent_start'
  | 'agent_end'
  | 'message_start'
  | 'message_end'
  | 'message_delta'
  | 'thinking_delta'
  | 'tool_execution_start'
  | 'tool_execution_update'
  | 'tool_execution_end'
  | 'queue_update'
  | 'session_compacted'
  | 'error'
  | 'member_cost';

/** Fields shared by every event. */
export interface BrainEventBase {
  /** Type discriminator. */
  type: BrainEventType;
  /** Unique event id (UUID v4 string). */
  id: string;
  /** Event timestamp (epoch ms). */
  ts: number;
  /** Session this event belongs to. */
  sessionId: string;
}

// --- Agent lifecycle --------------------------------------------------------

/**
 * The agent loop has started; carries the resolved model + provider.
 *
 * @since 0.5.0 `memberId` is included when the agent run belongs to a
 *          council member (i.e. the run was launched by the council
 *          pipeline, not by a direct user prompt). `memberName` is
 *          the human-readable label (e.g. "Caronte") for UI display
 *          without a second lookup into the roles table.
 */
export interface BrainAgentStartEvent extends BrainEventBase {
  type: 'agent_start';
  model: string;
  provider: string;
  memberId?: string;
  memberName?: string;
}

/**
 * The agent loop has finished, was cancelled, or errored out.
 *
 * @since 0.5.0 `memberId` + `memberName` mirror the corresponding
 *          `agent_start` (see above).
 */
export interface BrainAgentEndEvent extends BrainEventBase {
  type: 'agent_end';
  reason: 'completed' | 'cancelled' | 'error';
  durationMs: number;
  memberId?: string;
  memberName?: string;
}

// --- Message streaming ------------------------------------------------------

/**
 * An assistant message has begun streaming.
 *
 * @since 0.5.0 `memberId` + `memberName` identify the council member
 *          producing this message (omitted for direct user prompts).
 */
export interface BrainMessageStartEvent extends BrainEventBase {
  type: 'message_start';
  messageId: string;
  role: 'assistant';
  memberId?: string;
  memberName?: string;
}

/**
 * An incremental chunk of assistant message text.
 *
 * @since 0.5.0 `memberId` + `memberName` mirror `message_start` so
 *          consumers can stream-attribute text to a council member
 *          without buffering until `message_end`.
 */
export interface BrainMessageDeltaEvent extends BrainEventBase {
  type: 'message_delta';
  messageId: string;
  delta: string;
  memberId?: string;
  memberName?: string;
}

/**
 * An assistant message has finished streaming.
 *
 * @since 0.5.0 `memberId` + `memberName` mirror `message_start`.
 */
export interface BrainMessageEndEvent extends BrainEventBase {
  type: 'message_end';
  messageId: string;
  totalLength: number;
  finishReason: string;
  memberId?: string;
  memberName?: string;
  /**
   * Real token usage reported by the provider (Task G.4). When the
   * provider sends an OpenAI-shaped `usage` chunk (gated by
   * `stream_options.include_usage`), this carries the actual numbers.
   * When missing, downstream consumers fall back to the
   * ~4-characters-per-token approximation that v3-B used.
   *
   * @see docs/plans/2026-06-29-anathema-coder-v3-G.md (Task G.4.4)
   */
  usage?: UsageBreakdown;
}

/**
 * Provider-reported token usage for one assistant turn (Task G.4).
 * The numbers come from the OpenAI-compatible `usage` field in the
 * final SSE chunk before `[DONE]`. Providers that don't honor
 * `stream_options.include_usage` will not emit this — consumers must
 * degrade gracefully to the approximation.
 */
export interface UsageBreakdown {
  /** Tokens in the prompt (system + user messages, including tool messages). */
  promptTokens: number;
  /** Tokens generated by the assistant. */
  completionTokens: number;
  /** Convenience: promptTokens + completionTokens. */
  totalTokens: number;
}

// --- Reasoning (optional) ---------------------------------------------------

/** An incremental chunk of the model's reasoning / thinking trace. */
export interface BrainThinkingDeltaEvent extends BrainEventBase {
  type: 'thinking_delta';
  messageId: string;
  delta: string;
}

// --- Tool execution ---------------------------------------------------------

/** A tool call has started executing. */
export interface BrainToolExecutionStartEvent extends BrainEventBase {
  type: 'tool_execution_start';
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
}

/** Progress update emitted while a tool is still running. */
export interface BrainToolExecutionUpdateEvent extends BrainEventBase {
  type: 'tool_execution_update';
  toolCallId: string;
  delta?: string;
}

/** A tool call has finished, successfully or with an error. */
export interface BrainToolExecutionEndEvent extends BrainEventBase {
  type: 'tool_execution_end';
  toolCallId: string;
  result: string;
  isError: boolean;
  durationMs: number;
}

// --- Queued user prompts (steering / follow-up) -----------------------------

/** The count of queued user prompts has changed. */
export interface BrainQueueUpdateEvent extends BrainEventBase {
  type: 'queue_update';
  queuedCount: number;
}

// --- Session compaction -----------------------------------------------------

/** The session history was compacted into a summary. */
export interface BrainSessionCompactedEvent extends BrainEventBase {
  type: 'session_compacted';
  summary: string;
  messagesRemoved: number;
}

// --- Errors -----------------------------------------------------------------

/** A recoverable, fatal, or cancellation error occurred. */
export interface BrainErrorEvent extends BrainEventBase {
  type: 'error';
  severity: 'recoverable' | 'fatal' | 'cancelled';
  message: string;
  code?: string;
}

// --- Council per-member cost (Task I.1, v3-I) ------------------------------

/**
 * Per-member cost report emitted at the end of a council member's run.
 * Carries the actual `MemberCost` payload produced by
 * `MemberCostTracker.record()` (see `electron/cli/councilCost.ts`).
 *
 * Note: we inline a structural type here instead of importing the
 * `MemberCost` type, to keep this events module free of CLI-side deps
 * (architecture rule: events.ts must be renderer/Node-loadable without
 * pulling in `electron/cli/*`).
 */
export interface BrainMemberCostEvent extends BrainEventBase {
  type: 'member_cost';
  cost: {
    memberId: string;
    name: string;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    durationMs: number;
    toolCalls: number;
    errored: boolean;
  };
}

/** Discriminated union of every event the brain can emit. */
export type BrainEvent =
  | BrainAgentStartEvent
  | BrainAgentEndEvent
  | BrainMessageStartEvent
  | BrainMessageDeltaEvent
  | BrainMessageEndEvent
  | BrainThinkingDeltaEvent
  | BrainToolExecutionStartEvent
  | BrainToolExecutionUpdateEvent
  | BrainToolExecutionEndEvent
  | BrainQueueUpdateEvent
  | BrainSessionCompactedEvent
  | BrainErrorEvent
  | BrainMemberCostEvent;

/**
 * Map from a {@link BrainEventType} discriminator to its concrete event type.
 * Lets callers say `BrainEventOf<'agent_start'>` instead of repeating the
 * `Extract<BrainEvent, { type: ... }>` dance.
 */
export type BrainEventOf<T extends BrainEventType> = Extract<BrainEvent, { type: T }>;

// --- Type guards ------------------------------------------------------------

export function isBrainAgentStartEvent(e: BrainEvent): e is BrainAgentStartEvent {
  return e.type === 'agent_start';
}

export function isBrainAgentEndEvent(e: BrainEvent): e is BrainAgentEndEvent {
  return e.type === 'agent_end';
}

export function isBrainMessageStartEvent(e: BrainEvent): e is BrainMessageStartEvent {
  return e.type === 'message_start';
}

export function isBrainMessageDeltaEvent(e: BrainEvent): e is BrainMessageDeltaEvent {
  return e.type === 'message_delta';
}

export function isBrainMessageEndEvent(e: BrainEvent): e is BrainMessageEndEvent {
  return e.type === 'message_end';
}

export function isBrainThinkingDeltaEvent(e: BrainEvent): e is BrainThinkingDeltaEvent {
  return e.type === 'thinking_delta';
}

export function isBrainToolExecutionStartEvent(e: BrainEvent): e is BrainToolExecutionStartEvent {
  return e.type === 'tool_execution_start';
}

export function isBrainToolExecutionUpdateEvent(e: BrainEvent): e is BrainToolExecutionUpdateEvent {
  return e.type === 'tool_execution_update';
}

export function isBrainToolExecutionEndEvent(e: BrainEvent): e is BrainToolExecutionEndEvent {
  return e.type === 'tool_execution_end';
}

export function isBrainQueueUpdateEvent(e: BrainEvent): e is BrainQueueUpdateEvent {
  return e.type === 'queue_update';
}

export function isBrainSessionCompactedEvent(e: BrainEvent): e is BrainSessionCompactedEvent {
  return e.type === 'session_compacted';
}

export function isBrainErrorEvent(e: BrainEvent): e is BrainErrorEvent {
  return e.type === 'error';
}

export function isBrainMemberCostEvent(e: BrainEvent): e is BrainMemberCostEvent {
  return e.type === 'member_cost';
}

// --- Constructor ------------------------------------------------------------

/**
 * Build a fully-formed {@link BrainEvent}, stamping the common `id` (UUID v4)
 * and `ts` (epoch ms) fields automatically. The caller supplies only the
 * variant-specific payload.
 *
 * Uses the global `crypto.randomUUID()` (Web Crypto), available in both Node 20+
 * and the browser/renderer — keeping this module free of Node-only imports.
 *
 * @example
 * const ev = createBrainEvent('message_delta', sessionId, { messageId, delta });
 */
export function createBrainEvent<T extends BrainEventType>(
  type: T,
  sessionId: string,
  data: Omit<BrainEventOf<T>, 'type' | 'id' | 'ts' | 'sessionId'>,
): BrainEventOf<T> {
  return {
    type,
    id: crypto.randomUUID(),
    ts: Date.now(),
    sessionId,
    ...data,
  } as BrainEventOf<T>;
}
